import type { Addresses } from 'api/types/addresses.types';
import { PlusIcon } from 'assets/icons';
import { ReactComponent as BackArrow } from 'assets/icons/arrow-back.svg';
import cx from 'classnames';
import Button from 'components/atoms/Button';
import { BUTTON_NAMES } from 'constant/button-names';
import { useConfig } from 'context/app-config';
import { AddressSheetContext, type AddressSheetContextProviderProps } from 'packages/shared/newOrder/provider/AddressSheetContextProvider';
import React, { useCallback, useContext } from 'react';
import Col from 'react-bootstrap/Col';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useBasePath } from 'utils/router/hooks/useBasePath';

import AddressBox from './addressBox';
import { ADDRESS_SOURCES } from './constants';
import styles from './styles.module.scss';

interface Props {
  addresses: Addresses.Address[];
  disabled?: boolean;
  actionButtonProps?: {
    isLoading?: boolean;
  };
}

const StyledPlusIcon = styled(PlusIcon)`
  & > g {
    & > g {
      fill: ${({ theme }) => theme.components.pages.pickupDropoff?.newAddress?.plusIcon};
    }
  }
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.components.pages.pickupDropoff?.newAddress?.textColor};
  margin-bottom: 0px;
  text-decoration: underline;
`;

const StyledBackButton = styled.button`
  style: none;
  background: ${({ theme }) => theme.colors.white};
  border: 0px;
  border-radius: 40px;
  height: 48px;
  padding: 0 25px;
`;

const AddressList: React.FC<Props> = ({ addresses, disabled, actionButtonProps }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { toggleSheet, onContinue } = useContext(AddressSheetContext) as AddressSheetContextProviderProps;
  const config = useConfig();
  const basePath = useBasePath();

  const onBackPressed = useCallback(() => {
    toggleSheet?.();
  }, [toggleSheet]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <ListGroup className={cx('mb-4', styles.addressesListContainer)} variant="flush">
            <StyledLink
              to="/newOrder/addAddress"
              // check if calling address page from new order
              // then it should goBack after adding address
              state={{
                isContinueOrderFlow: basePath === '/newOrder',
              }}
              className={cx('d-flex h4 font-weight-bold align-items-center')}
            >
              <StyledPlusIcon className="margin-end-3" />
              {t('common.newAddress')}
            </StyledLink>
            {addresses.map((address) => (
              <AddressBox address={address} key={address.id} disableEdit={address.source !== ADDRESS_SOURCES.WASHMEN} />
            ))}
          </ListGroup>
        </Col>
      </Row>
      <Row className="mt-7 justify-content-between" noGutters>
        {config?.showAddressSheetBackButton && (
          <Col xs={2}>
            <StyledBackButton onClick={onBackPressed}>
              <BackArrow />
            </StyledBackButton>
          </Col>
        )}
        <Col xs={config?.showAddressSheetBackButton ? 9 : 12}>
          <Button
            textColor={theme.components.pages.pickupDropoff?.bottomSheet?.button?.textColor}
            backgroundColor={theme.components.pages.pickupDropoff?.bottomSheet?.button?.background}
            block
            size="lg"
            onClick={onContinue ?? toggleSheet}
            disabled={disabled}
            isLoading={actionButtonProps?.isLoading}
            buttonName={BUTTON_NAMES.SELECT_ADDRESS}
          >
            {t('common.selectAddress')}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default AddressList;
